import Immutable from 'immutable';
import { batchActions } from 'redux-batched-actions';
import { call, put, select } from 'redux-saga/effects';
import uuid from 'uuid';
import { LayoutType } from 'const';
import { updateDragHotspotPosition } from 'containers/App/actions';
import { dragHotspotPosition } from 'containers/App/selectors';
import { updateArtboard } from 'containers/Artboards/actions';
import { activeArtboard } from 'containers/Artboards/selectors';
import { addRelations } from 'containers/Relations/actions';
import { updateSection } from 'containers/Sections/actions';
import { sections as sectionsSelector } from 'containers/Sections/selectors';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import { createCarousel } from 'factories/carouselFactory';
import { LayeredLayout, Relations } from 'models';
import { handleSagaError } from 'services/handleError';
import { createLayoutAndRelations } from 'utils/createLayoutAndRelations';
import { setLayout } from '../actions';
import * as Models from '../models';

export type CarouselItemType = 'slide' | 'customText' | 'controls';

export enum CAROUSEL_ITEM_TYPES {
  SLIDE = 'slide',
  CUSTOM_TEXT = 'customText',
  CONTROLS = 'controls',
}

export const CAROUSEL_STATIC_TYPES: CarouselItemType[] = [CAROUSEL_ITEM_TYPES.CUSTOM_TEXT, CAROUSEL_ITEM_TYPES.CONTROLS];
export const DROP_TARGET_LAYOUTS_CAROUSEL_ITEM_TYPES: CarouselItemType[] = [CAROUSEL_ITEM_TYPES.SLIDE];

export type CarouselItem = {
  type: CarouselItemType;
  carouselId: string;
  slideId?: string;
  slideNumber?: number;
};

export function createCarouselItem(type: CarouselItemType, carouselId: string, slideId?: string, slideNumber?: number): CarouselItem {
  return { type, carouselId, slideId, slideNumber };
}

function createCarouselItems(carouselId: string): CarouselItem[] {
  return [
    createCarouselItem(CAROUSEL_ITEM_TYPES.SLIDE, carouselId, uuid(), 1),
    createCarouselItem(CAROUSEL_ITEM_TYPES.SLIDE, carouselId, uuid(), 2),
    createCarouselItem(CAROUSEL_ITEM_TYPES.CUSTOM_TEXT, carouselId),
    createCarouselItem(CAROUSEL_ITEM_TYPES.CONTROLS, carouselId),
  ];
}

type Props = {
  sectionId: string;
  carousel: { id: string; sectionId: string };
  artboard: ReturnTypeSaga<typeof activeArtboard>;
  position: ReturnTypeSaga<typeof dragHotspotPosition>;
};

type ReturnType = {
  allLayouts: LayeredLayout[];
  allRelations: Relations[];
  updatedArtboard: ReturnTypeSaga<typeof activeArtboard>;
};

function createAllLayoutAndAllRelations(props: Props): ReturnType {
  const { sectionId, carousel, artboard, position } = props;
  const carouselId = carousel.id;
  const type = LayoutType.TEXT_ONE_COLUMN;

  const carouselItems = createCarouselItems(carouselId);
  const allRelations: Relations[] = [];
  const allLayouts: LayeredLayout[] = [];
  let updatedArtboard = artboard;

  carouselItems.forEach((item, index) => {
    const { layout, relations } = createLayoutAndRelations(
      { type, section: sectionId },
      { carouselItem: item },
    );
    allLayouts.push(layout);
    allRelations.push(relations);
    updatedArtboard = updatedArtboard.set('layoutIds', updatedArtboard.get('layoutIds').insert(position + index, layout.id));
  });

  return { allLayouts, allRelations, updatedArtboard };
}

export function* addCarousel(action: Models.Action.IAddCarousel): Generator {
  try {
    const { sectionId } = action.payload;
    const artboard: ReturnTypeSaga<typeof activeArtboard> = yield select(activeArtboard);
    const position: ReturnTypeSaga<typeof dragHotspotPosition> = yield select(dragHotspotPosition);
    const sections: ReturnTypeSaga<typeof sectionsSelector> = yield select(sectionsSelector);
    const section = sections.get(sectionId);

    const carousel = createCarousel({ sectionId });

    const { allLayouts, allRelations, updatedArtboard } = createAllLayoutAndAllRelations({
      sectionId,
      carousel,
      artboard,
      position,
    });

    const updatedSection = section.set('carouselIds', section.get('carouselIds', Immutable.List()).push(carousel.id));

    yield put(batchActions([
      saveAppState(),
      updateSection(updatedSection),
      ...allRelations.map(relation => addRelations(relation)),
      ...allLayouts.map(layout => setLayout(layout)),
      updateArtboard(updatedArtboard),
      updateDragHotspotPosition(null),
    ]));
  } catch (error) {
    yield call(handleSagaError, error, 'Layouts.addCarousel', 'AddCarousel');
  }
}
